/**
 * How large a point is drawn in the 3D viewer.
 *
 * The one viewer setting that is worth offering, and the reason it exists is
 * not taste: a sparse cloud of forty thousand points and one of four hundred
 * thousand need different point sizes to be readable, and the viewer cannot
 * pick for both. At the default, a thin reconstruction looks like dust; large,
 * a dense one becomes a solid blob that hides its own structure.
 *
 * It is a *display* preference, so it lives in `localStorage` beside the theme
 * rather than on the account: it is a property of the screen being used, and
 * a 4K workstation and a phone want different answers from the same person.
 * There is no endpoint for it and inventing one would be storing a number
 * about a monitor on a user row.
 *
 * The stored value is a name, not a pixel count. A number would be a knob with
 * no meaning attached, and the three names map to multipliers the viewer
 * applies on top of the device pixel ratio - so "Orta" is the same *apparent*
 * size on a retina screen and a 1080p one.
 */

export const POINT_SIZES = ["small", "medium", "large"] as const;
export type PointSize = (typeof POINT_SIZES)[number];

/** Multipliers on the base size, which is itself scaled by the pixel ratio. */
export const POINT_SIZE_SCALE: Record<PointSize, number> = {
  small: 0.6,
  medium: 1,
  large: 1.8,
};

const STORAGE_KEY = "sentycap.viewer.pointSize";
const DEFAULT: PointSize = "medium";

const isPointSize = (value: unknown): value is PointSize =>
  typeof value === "string" && (POINT_SIZES as readonly string[]).includes(value);

export function readPointSize(): PointSize {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isPointSize(stored) ? stored : DEFAULT;
  } catch {
    return DEFAULT;
  }
}

export function writePointSize(size: PointSize): void {
  try {
    if (size === DEFAULT) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, size);
  } catch {
    // Applied already for this session; failing to remember it is not worth
    // interrupting anyone over.
  }
  // The viewer may be mounted on another route in the same document - the
  // settings screen and a project's model tab are one SPA. `storage` only
  // fires in *other* tabs, so a same-document event is what makes the change
  // visible without a reload.
  window.dispatchEvent(new CustomEvent(POINT_SIZE_CHANGED));
}

export const POINT_SIZE_CHANGED = "sentycap:viewer-point-size";
